import React from 'react'
import { FormContextConsumer, FormContextType } from '#src/components/Path/FormContext'
import { DatePicker, Button, FullBlockLoader, CircleLoader } from '@deal/components'
import AirportTypeaheadInput from '#src/components/AirportTypeaheadInput'
import {
  CreateBookingFlightPlanComponent,
  FlightPlanState,
  FlightPlanLegUpsertRequestInput,
  FlightPlanQuestionComponent,
  FlightPlanQuestionFragment
} from '#src/generated/graphql'
import { withIdentity, WithIdentityProps } from '#src/containers/Identity'
import { IdQuestionProps } from '../'
import { RouteSelection } from '#src/typings'
import styles from './styles.css'
import Logger from '#src/services/logger'
import { toast } from 'react-toastify'

interface FlightPlanQuestionProps {
  existingFlightPlan?: FlightPlanQuestionFragment
}

interface Airport {
  displayName: string
  id: string
}

interface FlightPlanQuestionState {
  // We maintain our own loading state because of the multiple mutations
  loading: boolean
  currentRouteSelection: RouteSelection
  departureAirport?: Airport
  arrivalAirport?: Airport
  departDate?: Date
  returnDate?: Date
}

class FlightPlanQuestion extends React.Component<
  WithIdentityProps & IdQuestionProps & { context: FormContextType } & FlightPlanQuestionProps,
  FlightPlanQuestionState
> {
  constructor(
    props: WithIdentityProps &
      IdQuestionProps & { context: FormContextType } & FlightPlanQuestionProps
  ) {
    super(props)

    this.state = {
      currentRouteSelection: RouteSelection.ROUND_TRIP,
      loading: false
    }

    this.handleFlightPlanCreated = this.handleFlightPlanCreated.bind(this)
    this.handleSetCurrentRouteSelection = this.handleSetCurrentRouteSelection.bind(this)
    this.handleChangeFromAirport = this.handleChangeFromAirport.bind(this)
    this.handleChangeToAirport = this.handleChangeToAirport.bind(this)
    this.handleChangeDepartDate = this.handleChangeDepartDate.bind(this)
    this.handleChangeReturnDate = this.handleChangeReturnDate.bind(this)
  }

  public componentDidMount() {
    if (!this.props.question.required) {
      this.props.onFieldValidityChanged(true)
    }

    if (this.props.existingFlightPlan) {
      // This module only creates round trip and one way, so we don't need to account for multi-leg yet
      const departureLeg = this.props.existingFlightPlan.legs[0]
      const returnLeg = this.props.existingFlightPlan.legs[1]

      this.setState({
        currentRouteSelection: returnLeg ? RouteSelection.ROUND_TRIP : RouteSelection.ONE_WAY,
        departureAirport: {
          displayName: departureLeg.departureAirport ? departureLeg.departureAirport.name : '',
          id: departureLeg.departureAirport ? departureLeg.departureAirport.id : ''
        },
        arrivalAirport: {
          displayName: departureLeg.arrivalAirport ? departureLeg.arrivalAirport.name : '',
          id: departureLeg.arrivalAirport ? departureLeg.arrivalAirport.id : ''
        },
        departDate: departureLeg.departOn ? new Date(departureLeg.departOn) : undefined,
        returnDate: returnLeg && returnLeg.departOn ? new Date(returnLeg.departOn) : undefined
      })
    }
  }

  public handleFlightPlanCreated(flightPlanId: string) {
    this.props.onFieldValueChanged({ value: flightPlanId })

    this.props.context && this.props.context.onPageSubmitted()
  }

  public handleSetCurrentRouteSelection(routeSelection: RouteSelection) {
    this.setState({
      currentRouteSelection: routeSelection
    })
  }

  public handleChangeFromAirport(from: string, name: string) {
    this.setState({
      departureAirport: {
        id: from,
        displayName: name
      }
    })
  }

  public handleChangeToAirport(to: string, name: string) {
    this.setState({
      arrivalAirport: {
        id: to,
        displayName: name
      }
    })
  }

  public handleChangeDepartDate(departDate: Date) {
    this.setState({
      departDate
    })
  }

  public handleChangeReturnDate(returnDate: Date) {
    this.setState({
      returnDate
    })
  }

  public isAirportValid(airport?: Airport) {
    return airport && !!airport.id
  }

  public isFlightPlanValid() {
    const isRoundTrip = this.state.currentRouteSelection === RouteSelection.ROUND_TRIP
    const isOneWayValid =
      this.isAirportValid(this.state.departureAirport) &&
      this.isAirportValid(this.state.arrivalAirport) &&
      this.state.departDate

    if (isRoundTrip) {
      // A return date is required for a valid round trip fliht
      return isOneWayValid && this.state.returnDate
    } else {
      return isOneWayValid
    }
  }

  public render() {
    const { myself } = this.props

    return (
      <CreateBookingFlightPlanComponent
        onCompleted={response => {
          if (
            response &&
            response.createFlightPlan &&
            !response.createFlightPlan.hasError &&
            response.createFlightPlan.flightPlan
          ) {
            this.handleFlightPlanCreated(response.createFlightPlan.flightPlan.id)
          }
        }}
        onError={response => {
          this.setState({
            loading: false
          })

          Logger.captureException('Error creating flight plan in path', {
            error: response.message
          })

          toast.error(
            'There was an error creating the flight plan, please check the details and try again.'
          )
        }}
      >
        {createFlightPlanMutation => (
          <div className={styles.container}>
            <div className={styles.selector}>
              <button
                type="button"
                onClick={() => {
                  this.handleSetCurrentRouteSelection(RouteSelection.ROUND_TRIP)
                }}
                className={
                  this.state.currentRouteSelection === RouteSelection.ROUND_TRIP
                    ? styles.selected
                    : undefined
                }
              >
                Round trip
              </button>
              <button
                type="button"
                onClick={() => {
                  this.handleSetCurrentRouteSelection(RouteSelection.ONE_WAY)
                }}
                className={
                  this.state.currentRouteSelection === RouteSelection.ONE_WAY
                    ? styles.selected
                    : undefined
                }
              >
                One way
              </button>
            </div>
            <div className={styles.inputs}>
              <div className={styles.legRow}>
                <div className={styles.airport}>
                  <span className={styles.inputLabel}>Flying from</span>
                  <AirportTypeaheadInput
                    defaultValue={this.state.departureAirport}
                    onSetValue={this.handleChangeFromAirport}
                    className={styles.airportTypeahead}
                    label=""
                    placeholder="Enter city or airport"
                    labelHidden={true}
                    iconHidden={true}
                  />
                </div>
                <div className={styles.date}>
                  <span className={styles.inputLabel}>When</span>
                  <DatePicker
                    hideOnDayClick={true}
                    styleType="standard"
                    label=""
                    labelHidden={true}
                    iconPlacement="left"
                    selectedDay={this.state.departDate}
                    onDayChange={this.handleChangeDepartDate}
                    isTimePicker={true}
                    placeholder="Depart"
                  />
                </div>
              </div>
              <div className={styles.legRow}>
                {this.state.currentRouteSelection === RouteSelection.ONE_WAY ? (
                  <div className={styles.airportFull}>
                    <span className={styles.inputLabel}>Flying to</span>
                    <AirportTypeaheadInput
                      defaultValue={this.state.arrivalAirport}
                      onSetValue={this.handleChangeToAirport}
                      className={styles.airportTypeahead}
                      label=""
                      placeholder="Enter city or airport"
                      labelHidden={true}
                      iconHidden={true}
                    />
                  </div>
                ) : (
                  <>
                    <div className={styles.airport}>
                      <span className={styles.inputLabel}>Flying to</span>
                      <AirportTypeaheadInput
                        defaultValue={this.state.arrivalAirport}
                        onSetValue={this.handleChangeToAirport}
                        className={styles.airportTypeahead}
                        label=""
                        placeholder="Enter city or airport"
                        labelHidden={true}
                        iconHidden={true}
                      />
                    </div>
                    <div className={styles.date}>
                      <span className={styles.inputLabel}>When</span>
                      <DatePicker
                        hideOnDayClick={true}
                        styleType="standard"
                        label=""
                        iconPlacement="left"
                        selectedDay={this.state.returnDate}
                        onDayChange={this.handleChangeReturnDate}
                        minDate={this.state.departDate}
                        labelHidden={true}
                        isTimePicker={true}
                        placeholder="Return"
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className={styles.actions}>
              <Button
                className={styles.requestToBook}
                disabled={!this.isFlightPlanValid() || this.state.loading}
                onClick={() => {
                  this.setState({
                    loading: true
                  })

                  const leg1: FlightPlanLegUpsertRequestInput[] = [
                    {
                      departAt: this.state.departDate ? this.state.departDate.toISOString() : '',
                      departureAirportId: this.state.departureAirport
                        ? this.state.departureAirport.id
                        : '',
                      arrivalAirportId: this.state.arrivalAirport
                        ? this.state.arrivalAirport.id
                        : ''
                    }
                  ]
                  const leg2: FlightPlanLegUpsertRequestInput[] =
                    this.state.currentRouteSelection === RouteSelection.ROUND_TRIP
                      ? [
                          {
                            departAt: this.state.returnDate
                              ? this.state.returnDate.toISOString()
                              : '',
                            departureAirportId: this.state.arrivalAirport
                              ? this.state.arrivalAirport.id
                              : '',
                            arrivalAirportId: this.state.departureAirport
                              ? this.state.departureAirport.id
                              : ''
                          }
                        ]
                      : []
                  createFlightPlanMutation({
                    variables: {
                      input: {
                        state: FlightPlanState.ACTIVE,
                        legs: [...leg1, ...leg2],
                        consumerId: myself ? myself.id : null
                      }
                    }
                  })
                }}
              >
                {this.state.loading ? 'Submitting...' : 'Submit'}
              </Button>
            </div>
          </div>
        )}
      </CreateBookingFlightPlanComponent>
    )
  }
}

const ExpertSelectionQuestionWithContext: React.SFC<IdQuestionProps> = ({ question, ...rest }) => {
  return (
    <FormContextConsumer>
      {formData => {
        // All question input values are arrays regardless of the type (id, number, text, etc.)
        const flightPlanId =
          question.idUserInput.value.length > 0 ? question.idUserInput.value[0] : null

        return (
          <FlightPlanQuestionComponent
            variables={{
              id: flightPlanId
            }}
            skip={!flightPlanId}
          >
            {({ data, loading }) => {
              if (loading) {
                return <FullBlockLoader loader={CircleLoader} />
              } else if (data && data.flightPlan) {
                return (
                  <FlightPlanQuestion
                    question={question}
                    existingFlightPlan={data.flightPlan}
                    context={formData}
                    {...rest}
                  />
                )
              }

              return <FlightPlanQuestion question={question} context={formData} {...rest} />
            }}
          </FlightPlanQuestionComponent>
        )
      }}
    </FormContextConsumer>
  )
}

export default withIdentity(ExpertSelectionQuestionWithContext)
